import { ScreenManager, ScreenManagerEvents, ScreenTransitionDirection } from "../../managers/ScreenManager";
import { LoaderFileKeys } from "../constant/AppConstants";
import { ScreenOrientationType } from "../../utils/GetOrientation";
import { IconButton } from "../../ui/IconButton";
import { LabelButton } from "../../ui/LabelButton";
import { LobbyVariationItem } from "./lobby/LobbyVariationItem";
import { RectSize } from "../../utils/RectSize";
import { ApplyMixins } from "../../utils/ApplyMixins";
import { Showable } from "../../mixins/Showable";
import { AppSettings } from "../managers/AppSettings";
import { AppVersionNumber } from "../ui/AppVersionNumber";
import { VariationId } from "./TableScreen";
import { AppLocalStorage } from "../managers/AppLocalStorage";
import { LiveDataChangeEvents, OptionsDataManager } from "../managers/OptionsDataManager";
import { LayoutDataManager, OptionsItemKeys } from "../managers/LayoutDataManager";
import { AppLocalization, LanguageId } from "../localization/AppLocalization";
import { ThemeDataManager } from "../managers/ThemeDataManager";
import { GetFixedCollectionIndex } from "../../utils/GetFixedCollectionIndex";
import { LobbyHeaderProps, LobbyVariationItemDef, LobbyVariationItemsProps, OptionsItemThemeItemData, OptionsLiveData, SharedLayoutLobbyData } from "../../types/types";
import { ToggleButton } from "../../ui/ToggleButton";
import { AppDebug } from "../debug/AppDebug";
import { AppAudioPlayer, SoundEffectKeys } from "../../audio/AppAudioPlayer";
import { ButtonEvents } from "../../ui/ButtonBase";
import { LocalizationEvents } from "../localization/Localization";
import { IsIPad } from "../../utils/IsIPad";
import { GTMTracking } from "../managers/GTMTracking";
import { GetBrowserString } from "../../utils/GetBrowserString";
import { GetOSString } from "../../utils/GetOSString";
import { AppTimings } from "../data/AppConstants";


// linting
export interface LobbyScreen extends Showable { }


export enum LobbyLayout {
    /**
     * Showing all variations
     */
    MULTI,
    /**
     * Showing single variation
     */
    SINGLE
}

export class LobbyScreen
    extends Phaser.GameObjects.Container {
    private static appliedMixins: boolean = false;
    public btnBurger: IconButton;
    public btnBack: IconButton;
    public btnPrev: IconButton;
    public btnNext: IconButton;
    public btnNewGame: LabelButton;
    public btnContinue: LabelButton;
    public btnFullscreen: ToggleButton;
    public btnMusic: ToggleButton;
    public btnSound: ToggleButton;
    public itemsContainer: Phaser.GameObjects.Container;
    public versionNumber: AppVersionNumber;
    public transitionDirection: ScreenTransitionDirection = ScreenTransitionDirection.RIGHT_TO_CENTER;
    private items: LobbyVariationItem[];
    private curItemIndex: number;
    private burgerClickCallback: Function;
    private newGameClickCallback: Function;
    private continueClickCallback: Function;
    private onLobbyVariationHelpClick: Function;
    private onLobbyVariationStatsClick: Function;
    private curLayout: LobbyLayout;
    private headerProps: LobbyHeaderProps;

    public Init(
        burgerClickCallback: Function,
        newGameClickCallback: Function,
        continueClickCallback: Function,
        onLobbyVariationHelpClick: Function,
        onLobbyVariationStatsClick: Function,
    ): void {
        this.visible = false;

        if (!LobbyScreen.appliedMixins) {
            ApplyMixins(LobbyScreen, [Showable]);
            LobbyScreen.appliedMixins = true;
        }

        // this.name = "LobbyScreen";

        this.SetTweenProps(
            // showTweenConfig
            {
                onStart: this.OnShowStart.bind(this),
                // onComplete: this.OnShowComplete.bind(this),
            },
            // hideTweenConfig
            {
                onStart: this.OnHideStart.bind(this),
            },
            // showTweenTargetProps
            {}, // remove defaults (alpha transition)
            // hideTweenTargetProps
            {}, // remove defaults (alpha transition)
        );

        this.burgerClickCallback = burgerClickCallback;
        this.newGameClickCallback = newGameClickCallback;
        this.continueClickCallback = continueClickCallback;
        this.onLobbyVariationHelpClick = onLobbyVariationHelpClick;
        this.onLobbyVariationStatsClick = onLobbyVariationStatsClick;

        this.items = [];
        this.headerProps = LayoutDataManager.GetSharedData().lobby.header;
    }

    public Build(): void {
        this.itemsContainer = this.scene.make.container({});
        this.add(this.itemsContainer);

        // version
        this.versionNumber = new AppVersionNumber(this.scene);
        let verString = "v" + this.scene.sys.game.config.gameVersion;
        this.versionNumber.Init(verString, {
            font: "28px Arial",
            color: "#ddd",
            metrics: AppSettings.metrics.arial28,
        } as any);
        this.versionNumber.Build();
        this.add(this.versionNumber);

        // items (variations)
        const lobbyLayoutData: SharedLayoutLobbyData = LayoutDataManager.GetSharedData().lobby;
        const numVariations = lobbyLayoutData.items.length;
        const landscapeMultiStartX: number = -lobbyLayoutData.itemProps.landscape.itemSpacing.x * 0.5 * (numVariations - 1);
        const portraitMultiStartY: number = -lobbyLayoutData.itemProps.portrait.itemSpacing.y * 0.5 * (numVariations - 1);
        for (let i = 0; i < numVariations; i++) {
            const itemDef = lobbyLayoutData.items[i];
            const item = new LobbyVariationItem(this.scene);
            itemDef.landscape.single.x = 0;
            itemDef.landscape.single.y = 0;
            itemDef.portrait.single.x = 0;
            itemDef.portrait.single.y = 0;
            itemDef.landscape.multi.x = lobbyLayoutData.itemProps.landscape.x + landscapeMultiStartX + i * lobbyLayoutData.itemProps.landscape.itemSpacing.x;
            itemDef.landscape.multi.y = lobbyLayoutData.itemProps.landscape.y;
            itemDef.portrait.multi.x = lobbyLayoutData.itemProps.portrait.x;
            itemDef.portrait.multi.y = lobbyLayoutData.itemProps.portrait.y + portraitMultiStartY + i * lobbyLayoutData.itemProps.portrait.itemSpacing.y;
            item.Init(
                itemDef.variationId,
                // bgTextureData
                { texture: LoaderFileKeys.lobby, frame: itemDef.bg.frame },
                // logoTextureData
                { texture: LoaderFileKeys.lobby, frame: itemDef.logo.frame },
                lobbyLayoutData.itemProps,
            );
            item.Build();
            item.SetHitAreaRectangle(new Phaser.Geom.Rectangle(-550, -310, 1100, 620));
            // item.SetInteractive(false); // @see SetLayout
            item.Hide(false);
            this.itemsContainer.add(item);
            this.items.push(item);
        }

        // btnPrev (for LobbyLayout.MULTI only)
        if (numVariations > 1) {
            this.btnPrev = new IconButton(this.scene, -680);
            this.btnPrev.InitBase({
                out: { texture: LoaderFileKeys.ui, frame: "btn-round-bg-out.png" },
                over: { texture: LoaderFileKeys.ui, frame: "btn-round-bg-over.png" },
                disabled: { texture: LoaderFileKeys.ui, frame: "btn-round-bg-disabled.png" },
            });
            this.btnPrev.InitIcon({
                out: { texture: LoaderFileKeys.ui, frame: "icon-play-black.png" }
            });
            this.btnPrev.PostInit(60);
            // this.btnPrev.setScale(1.1);
            this.btnPrev.icon.scaleX = -1;
            this.btnPrev.icon.x -= 7;
            // shadow
            const btnPrevShadow = this.scene.make.image({ key: LoaderFileKeys.ui, frame: "circle-01-shadow.png" }).setScale(1.15);
            this.btnPrev.add(btnPrevShadow);
            this.btnPrev.sendToBack(btnPrevShadow);
            this.itemsContainer.add(this.btnPrev);
        }

        // btnNext (for LobbyLayout.MULTI only)
        if (numVariations > 1) {
            this.btnNext = new IconButton(this.scene, 680);
            this.btnNext.InitBase({
                out: { texture: LoaderFileKeys.ui, frame: "btn-round-bg-out.png" },
                over: { texture: LoaderFileKeys.ui, frame: "btn-round-bg-over.png" },
                disabled: { texture: LoaderFileKeys.ui, frame: "btn-round-bg-disabled.png" },
            });
            this.btnNext.InitIcon({
                out: { texture: LoaderFileKeys.ui, frame: "icon-play-black.png" }
            });
            this.btnNext.PostInit(60);
            // this.btnNext.setScale(1.1);
            this.btnNext.icon.x += 7;
            // shadow
            const btnNextShadow = this.scene.make.image({ key: LoaderFileKeys.ui, frame: "circle-01-shadow.png" }).setScale(1.15);
            this.btnNext.add(btnNextShadow);
            this.btnNext.sendToBack(btnNextShadow);
            this.itemsContainer.add(this.btnNext);
        }

        // burger
        this.btnBurger = new IconButton(this.scene);
        this.btnBurger.InitBase({
            out: { texture: LoaderFileKeys.ui, frame: "btn-round-bg-out.png" },
            over: { texture: LoaderFileKeys.ui, frame: "btn-round-bg-over.png" },
            disabled: { texture: LoaderFileKeys.ui, frame: "btn-round-bg-disabled.png" },
        });
        this.btnBurger.InitIcon({
            out: { texture: LoaderFileKeys.ui, frame: "icon-burger-black.png" }
        });
        this.btnBurger.PostInit(60);
        // shadow
        const btnBurgerShadow = this.scene.make.image({ key: LoaderFileKeys.ui, frame: "circle-01-shadow.png" }).setScale(1.15);
        this.btnBurger.add(btnBurgerShadow);
        this.btnBurger.sendToBack(btnBurgerShadow);
        this.add(this.btnBurger);

        // btnBack (for LobbyLayout.MULTI only)
        if (numVariations > 1) {
            this.btnBack = new IconButton(this.scene);
            this.btnBack.InitBase({
                out: { texture: LoaderFileKeys.ui, frame: "btn-round-bg-out.png" },
                over: { texture: LoaderFileKeys.ui, frame: "btn-round-bg-over.png" },
                disabled: { texture: LoaderFileKeys.ui, frame: "btn-round-bg-disabled.png" },
            });
            this.btnBack.InitIcon({
                out: { texture: LoaderFileKeys.ui, frame: "icon-back-black.png" }
            });
            this.btnBack.PostInit(40);        // shadow
            const btnLobbyShadow = this.scene.make.image({ key: LoaderFileKeys.ui, frame: "circle-01-shadow.png" }).setScale(1.15);
            this.btnBack.add(btnLobbyShadow);
            this.btnBack.sendToBack(btnLobbyShadow);
            this.add(this.btnBack);
        }

        // btnNewGame
        this.btnNewGame = new LabelButton(this.scene);
        this.btnNewGame.InitBase({
            out: { texture: LoaderFileKeys.ui, frame: "btn-oval2-bg-out.png" },
            over: { texture: LoaderFileKeys.ui, frame: "btn-oval2-bg-over.png" },
            disabled: { texture: LoaderFileKeys.ui, frame: "btn-oval2-bg-disabled.png" },
        });
        this.btnNewGame.InitLabel(
            {
                out: {
                    color: "#000",
                    fontSize: "50px",
                    fontFamily: AppSettings.fontFamilies.MontserratBold,
                    metrics: AppSettings.metrics.montserratBold50,
                },
            }
        );
        this.btnNewGame.label.setOrigin(0.5);
        this.btnNewGame.PostInit(20);
        this.add(this.btnNewGame);

        // btnContinue
        this.btnContinue = new LabelButton(this.scene);
        this.btnContinue.InitBase({
            out: { texture: LoaderFileKeys.ui, frame: "btn-oval2-bg-out.png" },
            over: { texture: LoaderFileKeys.ui, frame: "btn-oval2-bg-over.png" },
            disabled: { texture: LoaderFileKeys.ui, frame: "btn-oval2-bg-disabled.png" },
        }); // disabled
        this.btnContinue.InitLabel(
            {
                out: {
                    color: "#000",
                    fontSize: "50px",
                    fontFamily: AppSettings.fontFamilies.MontserratBold,
                    metrics: AppSettings.metrics.montserratBold50,
                },
            }
        );
        this.btnContinue.label.setOrigin(0.5);
        this.btnContinue.PostInit(20);
        this.btnContinue.SetInteractive(false);
        this.add(this.btnContinue);

        // fullscreen toggle button
        const isFullscreenSupported: boolean = this.scene.scale.fullscreen.available && !AppDebug.HasForceHideFullscreenBtn();
        if (isFullscreenSupported) {
            const isFullScreen: boolean = this.scene.scale.isFullscreen;
            // NOTE: fs always initially OFF. Need user click to ON.
            this.btnFullscreen = new ToggleButton(this.scene);
            this.btnFullscreen.InitBase({
                out: { texture: LoaderFileKeys.ui, frame: "btn-fs-on-out.png" },
                over: { texture: LoaderFileKeys.ui, frame: "btn-fs-on-over.png" }
            }, true);
            this.btnFullscreen.InitToggle({
                out: { texture: LoaderFileKeys.ui, frame: "btn-fs-on-off-out.png" },
                over: { texture: LoaderFileKeys.ui, frame: "btn-fs-on-off-over.png" }
            }, isFullScreen);
            this.btnFullscreen.PostInit(30);
            this.btnFullscreen.setScale(0.85);
            this.add(this.btnFullscreen);
        }

        // music toggle button
        const isMusicEnabled: boolean = AppLocalStorage.GetMusicEnabled();
        this.btnMusic = new ToggleButton(this.scene);
        this.btnMusic.name = "btnMusic-LobbyScene";
        this.btnMusic.InitBase({
            out: { texture: LoaderFileKeys.ui, frame: "btn-music-off-out.png" },
            over: { texture: LoaderFileKeys.ui, frame: "btn-music-off-over.png" }
        },
            true
        );
        this.btnMusic.InitToggle({
            out: { texture: LoaderFileKeys.ui, frame: "btn-music-on-out.png" },
            over: { texture: LoaderFileKeys.ui, frame: "btn-music-on-over.png" }
        },
            isMusicEnabled
        );
        this.btnMusic.PostInit(30);
        this.btnMusic.setScale(0.8);
        this.add(this.btnMusic);

        // sounds toggle button
        const isSoundEnabled: boolean = AppLocalStorage.GetSoundsEnabled();
        this.btnSound = new ToggleButton(this.scene);
        this.btnSound.name = "btnSound-LobbyScene";
        this.btnSound.InitBase({
            out: { texture: LoaderFileKeys.ui, frame: "btn-sound-off-out.png" },
            over: { texture: LoaderFileKeys.ui, frame: "btn-sound-off-over.png" }
        },
            true
        );
        this.btnSound.InitToggle({
            out: { texture: LoaderFileKeys.ui, frame: "btn-sound-on-out.png" },
            over: { texture: LoaderFileKeys.ui, frame: "btn-sound-on-over.png" }
        },
            isSoundEnabled
        );
        this.btnSound.PostInit(30);
        this.btnSound.setScale(0.8);
        this.add(this.btnSound);

        const lastVariationId: VariationId = AppLocalStorage.GetLastPlayedVariationId(null);
        // if we did play a variation before
        if (lastVariationId != null) {
            // set layout to SINGLE and focus this variation
            this.curItemIndex = LayoutDataManager.GetLobbyVariationItemIndexByVariationId(lastVariationId);
            this.SetLayout(LobbyLayout.SINGLE);
        }
        // not visited a variation yet (first time run of data wipe)
        else {
            // set MULTI or SINGLE layout based on total number of variation items
            // this.curItemIndex = this.GetItemIndexByVariationId(lastVariationId);
            if (numVariations > 1) {
                this.SetLayout(LobbyLayout.MULTI);
            }
            else {
                this.curItemIndex = LayoutDataManager.GetDefaultItemIndex();
                this.SetLayout(LobbyLayout.SINGLE);
            }
        }
		console.log("lastVariationId="+lastVariationId);

        // set choiceindex from cache once for all variation items' sliders
        // const optionsSharedLiveData: OptionsLiveData = OptionsDataManager.GetSharedData();
        // set default items' slider's data
        // const langId: LanguageId = AppLocalization.GetCurLanguageId();
        // const optionsSharedLiveData: OptionsLiveData = OptionsDataManager.GetSharedData();
        const choices = AppLocalization.GetSharedData().options.itemsById[OptionsItemKeys.pointsPerGame].choices;
        this.items.forEach(item => {
            item.slider.SetChoices(choices.map(choice => choice.itemLabel as string));
            item.slider.SetStartChoiceIndex(0);
            // set live data via LobbyScreen
            // this.OnPointsPerGameChange(OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.pointsPerGame, item.variationId).value as number);
            item.slider.SetChoiceIndex(OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.pointsPerGame).value as number);
        });

        this.OnLanguageChanged();

        // perma-listeners
        // BACKGROUND_CHANGE
        // OptionsDataManager.GetEventEmitter().on(LiveDataChangeEvents.BACKGROUND_CHANGE, this.OnBackgroundDataChange, this);
        // POINTS_PER_GAME_CHANGE
        OptionsDataManager.GetEventEmitter().on(LiveDataChangeEvents.POINTS_PER_GAME_CHANGE, this.OnPointsPerGameChange, this);
        // listen to language change
        AppLocalization.GetEmitter().on(LocalizationEvents.LANGUAGE_CHANGED, this.OnLanguageChanged, this);
    }

    private OnLanguageChanged(languageId?: LanguageId): void {
        // console.log("OnLanguageChange", LanguageId[languageId]);
        languageId = languageId ?? AppLocalization.GetCurLanguageId();

        this.btnNewGame.label.text = AppLocalization.GetSharedData(languageId).lobby.btnNewGame.itemLabel;
        this.btnContinue.label.text = AppLocalization.GetSharedData(languageId).lobby.btnContinue.itemLabel;
    }

    public GetCurItemIndex(): number {
        return this.curItemIndex;
    }

    public GetCurVariationId(): VariationId | null {
        return this.curItemIndex != undefined ? this.GetVariationIdByItemIndex(this.curItemIndex) : null;
    }

    public SetCurVariationId(id: VariationId): void {
        this.curItemIndex = LayoutDataManager.GetLobbyVariationItemIndexByVariationId(id);
    }

    public GetVariationIdByItemIndex(itemIndex: number): VariationId {
        // console.log(LobbyScreen.instance.itemsDefs, itemIndex)
        const itemsDefs: LobbyVariationItemDef[] = LayoutDataManager.GetSharedData().lobby.items;
        return itemsDefs[itemIndex].variationId;
    }

    /**
     * Show one (LAYOUT_SINGLE) or all (MULTI) lobby variation items
     * NOTE: only visibility/animation here
     * NOTE: positions & scale in @see OnResize
     * 
     * @param newLayout 
     * @param fromUserInteraction additionally tween positions of fs, music, sounds and call manual resize.
     */
    public SetLayout(
        newLayout: LobbyLayout,
        force: boolean = false,
        onResize: boolean = false,
    ): void {
        // console.log("SetLayout");
        // console.log(onResize);

        if (force || (this.curLayout != newLayout)) {
            // console.log(this.curLayout,"->",newLayout);
            this.curLayout = newLayout;

            // variation item's interactivity
            switch (newLayout) {
                case LobbyLayout.MULTI:
                    this.items.forEach(item => item.SetInteractive(true));
                    break;

                case LobbyLayout.SINGLE:
                    this.items.forEach(item => item.SetInteractive(false));
                    break;
            }

            // update variation items
            const screenOrientationType: ScreenOrientationType = ScreenManager.GetOrientation();
            this.UpdateItemsVisibility();
            this.UpdateItemsTransforms(screenOrientationType);
            this.UpdateItemsInteractive();

            this.OnResizeManual(true); // fromLayoutSwitch?
        }

    }

    /**
     * Update visibility of all items
     */
    private UpdateItemsVisibility(): void {
        // const screenOrientationType: ScreenOrientationType = ScreenManager.GetOrientation();
        switch (this.curLayout) {
            case LobbyLayout.MULTI:
                this.items.forEach((item: LobbyVariationItem, itemIndex: number) => {
                    // item.alpha = 1;
                    if (!item.IsShowing()) {
                        item.Show(true);
                    }
                    item.SetLayout(LobbyLayout.MULTI);
                });
                this.btnNewGame.visible = false;
                this.btnContinue.visible = false;
                this.btnBack.visible = false;
                this.btnPrev.visible = false;
                this.btnNext.visible = false;
                this.btnBurger.visible = false;
                break;

            case LobbyLayout.SINGLE:
                this.items.forEach((item: LobbyVariationItem, itemIndex: number) => {
                    if (itemIndex == this.curItemIndex) {
                        // item.alpha = 1;
                        if (!item.IsShowing()) {
                            // Only using internal mixin callbacks
                            item.Show(false);
                        }
                        // subbutton visibility
                        item.SetLayout(LobbyLayout.SINGLE);
                    }
                    else {
                        // item.alpha = 0;
                        if (item.IsShowing()) {
                            // Only using internal mixin callbacks
                            item.Hide(false);
                        }
                    }
                });
                this.btnNewGame.visible = true;
                this.btnContinue.visible = true;
                this.btnBack?.setVisible(true);
                this.btnPrev?.setVisible(true);
                this.btnNext?.setVisible(true);
                this.btnBurger.visible = true;
                break;
        }
    }

    /**
     * Update transforms (position, scale) or all on-screen elements.
     * 
     * @param screenOrientationType Current screen orientation
     */
    private UpdateItemsTransforms(
        screenOrientationType: ScreenOrientationType,
        animate: boolean = true,
    ): void {
        // console.log("UpdateItemsTransforms");

        const windowRect: RectSize = ScreenManager.GetWindowRect();
        const btnScale = ScreenManager.GetAssetScale(
            LayoutDataManager.GetSharedData().general.screen.closeButton.baseScale,
            LayoutDataManager.GetSharedData().general.screen.closeButton.scaleBias,
        );
        const btnOffset: number = this.headerProps.btnOffset * btnScale;
        const btnSpacing: number = this.headerProps.btnSpacing * btnScale;

        let btnMusicPosX: number, btnMusicPosY: number, btnSoundPosX: number, btnSoundPosY: number, btnFsPosX: number, btnFsPosY: number = 0;
        let newGameBtnScale: number = 0;
        switch (screenOrientationType) {
            case ScreenOrientationType.LANDSCAPE:
                switch (this.curLayout) {
                    case LobbyLayout.MULTI:
                        btnMusicPosX = windowRect.width - btnOffset;
                        btnMusicPosY = btnOffset;
                        btnSoundPosX = windowRect.width - btnOffset - btnSpacing;
                        btnSoundPosY = btnOffset;
                        btnFsPosX = windowRect.width - btnOffset - 2 * btnSpacing;
                        btnFsPosY = btnOffset;
                        break;
                    case LobbyLayout.SINGLE:
                        btnMusicPosX = windowRect.width - btnOffset - btnSpacing;
                        btnMusicPosY = btnOffset;
                        btnSoundPosX = windowRect.width - btnOffset - 2 * btnSpacing;
                        btnSoundPosY = btnOffset;
                        btnFsPosX = windowRect.width - btnOffset - 3 * btnSpacing;
                        btnFsPosY = btnOffset;
                        newGameBtnScale = ScreenManager.GetAssetScale(1.0, 1.0);
                        break;
                }
                break;

            case ScreenOrientationType.PORTRAIT:
                switch (this.curLayout) {
                    case LobbyLayout.MULTI:
                        btnMusicPosX = windowRect.width - btnOffset;
                        btnMusicPosY = btnOffset;
                        btnSoundPosX = windowRect.width - btnOffset - btnSpacing;
                        btnSoundPosY = btnOffset;
                        btnFsPosX = windowRect.width - btnOffset - 2 * btnSpacing;
                        btnFsPosY = btnOffset;
                        break;
                    case LobbyLayout.SINGLE:
                        btnMusicPosX = windowRect.width - btnOffset - btnSpacing;
                        btnMusicPosY = btnOffset;
                        btnSoundPosX = windowRect.width - btnOffset - 2 * btnSpacing;
                        btnSoundPosY = btnOffset;
                        btnFsPosX = windowRect.width - btnOffset - 3 * btnSpacing;
                        btnFsPosY = btnOffset;
                        newGameBtnScale = ScreenManager.GetAssetScale(1.4, 1.0) * 1.4;
                        break;
                }
                break;
        }

        // btnBack position never changes (but scale might [slightly]? so set it anyway?)
        this.btnBack?.setPosition(btnOffset, btnOffset);
        if (animate) {
            // NOTE: UpdateItemVisibility ran before this; 
            //       at this point btnBurger is either hidden or shows; animate it in or out
            this.scene.tweens.add({
                targets: this.btnBurger,
                scale: this.btnBurger.visible ? btnScale : 0,
                duration: 500,
                ease: AppSettings.ease.genericShowHideEase,
            });
            // to show burger fadeout, it must be visible
            this.btnBurger.visible = true;
            this.scene.tweens.add({
                targets: this.btnMusic,
                x: btnMusicPosX,
                y: btnMusicPosY,
                duration: 500,
                ease: AppSettings.ease.genericShowHideEase,
            });
            this.scene.tweens.add({
                targets: this.btnSound,
                x: btnSoundPosX,
                y: btnSoundPosY,
                duration: 500,
                ease: AppSettings.ease.genericShowHideEase,
            });
            if (this.btnFullscreen) { // only true if not taken away by GET attribute and NOT on iOS
                this.scene.tweens.add({
                    targets: this.btnFullscreen,
                    x: btnFsPosX,
                    y: btnFsPosY,
                    duration: 500,
                    ease: AppSettings.ease.genericShowHideEase,
                });
            }
            if (this.btnBack) { // only true if numVariations>1
                // NOTE: same as @see btnBurger
                this.scene.tweens.add({
                    targets: this.btnBack,
                    scale: this.btnBack.visible ? btnScale : 0,
                    duration: 500,
                    ease: AppSettings.ease.genericShowHideEase,
                });
                this.btnBack.visible = true;
            }
            // NEW, CONTINUE
            this.scene.tweens.add({
                targets: this.btnNewGame,
                scale: this.btnNewGame.visible ? newGameBtnScale : 0,
                duration: 500,
                ease: AppSettings.ease.genericShowHideEase,
            });
            this.scene.tweens.add({
                targets: this.btnContinue,
                scale: this.btnContinue.visible ? newGameBtnScale : 0,
                duration: 500,
                ease: AppSettings.ease.genericShowHideEase,
            });
        }
        else {
            this.btnBurger.scale = this.btnBurger.visible ? btnScale : 0;
            this.btnBurger.visible = true;
            this.btnMusic.setPosition(btnMusicPosX, btnMusicPosY);
            this.btnSound.setPosition(btnSoundPosX, btnSoundPosY);
            if (this.btnFullscreen) {
                this.btnFullscreen.setPosition(btnFsPosX, btnFsPosY);
            }
            if (this.btnBack) {
                this.btnBack.scale = this.btnBack.visible ? btnScale : 0;
                this.btnBack.visible = true;
            }
            this.btnNewGame.scale = this.btnNewGame.visible ? newGameBtnScale : 0;
            this.btnContinue.scale = this.btnContinue.visible ? newGameBtnScale : 0;
            this.btnNewGame.scale = newGameBtnScale;
            this.btnContinue.scale = newGameBtnScale;
        }

        const itemsDefs: LobbyVariationItemDef[] = LayoutDataManager.GetSharedData().lobby.items;
        // update position and scale of VariationItems
        switch (screenOrientationType) {
            case ScreenOrientationType.LANDSCAPE:
                switch (this.curLayout) {
                    case LobbyLayout.MULTI:
                        // landscape, show all
                        this.items.forEach((item, i) => {
                            item.setPosition(
                                itemsDefs[i].landscape.multi.x,
                                itemsDefs[i].landscape.multi.y,
                            );
                            item.setScale(itemsDefs[i].landscape.multi.scale);
                        });
                        break;
                    case LobbyLayout.SINGLE:
                        // landscape, show single
                        const item = this.items[this.curItemIndex];
                        item.setPosition(
                            itemsDefs[this.curItemIndex].landscape.single.x,
                            itemsDefs[this.curItemIndex].landscape.single.y,
                        );
                        item.setScale(itemsDefs[this.curItemIndex].landscape.single.scale);
                        this.btnPrev?.setY(item.y);
                        this.btnNext?.setY(item.y);
                        break;
                }
                break;

            case ScreenOrientationType.PORTRAIT:
                switch (this.curLayout) {
                    case LobbyLayout.MULTI:
                        // portrait, show all
                        this.items.forEach((item, i) => {
                            item.setPosition(
                                itemsDefs[i].portrait.multi.x,
                                itemsDefs[i].portrait.multi.y,
                            );
                            item.setScale(itemsDefs[i].portrait.multi.scale);
                        });
                        break;
                    case LobbyLayout.SINGLE:
                        // portrait, show single
                        const item = this.items[this.curItemIndex];
                        item.setPosition(
                            itemsDefs[this.curItemIndex].portrait.single.x,
                            itemsDefs[this.curItemIndex].portrait.single.y,
                        );
                        item.setScale(itemsDefs[this.curItemIndex].portrait.single.scale);
                        this.btnPrev?.setY(item.y);
                        this.btnNext?.setY(item.y);
                        break;
                }
                break;
        }
    }

    /**
     * Update interactivity of buttons that provide entry to Table
     */
    private UpdateItemsInteractive(): void {
        const variationId: VariationId = this.GetCurVariationId();
        if (variationId != null) {
            const hasTableData: boolean = AppLocalStorage.GetTableData(variationId) != null;
            switch (variationId) {
                case VariationId.BELOTE:
                    this.btnNewGame.SetInteractive(true);
                    this.btnContinue.SetInteractive(hasTableData);
                    break;
                case VariationId.COINCHE:
                    this.btnNewGame.SetInteractive(true);
                    this.btnContinue.SetInteractive(hasTableData);
                    break;
                case VariationId.CONTREE:
                    this.btnNewGame.SetInteractive(true);
                    this.btnContinue.SetInteractive(hasTableData);
                    break;
            }

            const variationIndex = LayoutDataManager.GetLobbyVariationItemIndexByVariationId(variationId);
            this.items.forEach((item, i) => {
                // console.log(i,"interactive?",(variationIndex == i));
                item.SetButtonsInteractive(variationIndex == i);
            });
        }
    }

    public OnItemClicked(): void {
        // console.log("OnItemClicked");

        const _this: LobbyScreen = this["_this"];

        // about to transition to table
        // if (clickedItemIndex != _this.curItemIndex) {
        //     _this.FocusItemByItemIndex(clickedItemIndex);
        // }
        if (_this.curLayout == LobbyLayout.MULTI) {
            const clickedItem: LobbyVariationItem = this["item"];
            const clickedItemIndex = _this.items.indexOf(clickedItem);
            _this.curItemIndex = clickedItemIndex;

            // console.log("MULTI -> SINGLE")
            _this.SetLayout(LobbyLayout.SINGLE); // force? onResize?

            // play button click
            AppAudioPlayer.PlaySfx(SoundEffectKeys.click5);
        }
    }

    private OnBackClick(): void {
        // console.log("OnBackClick",LobbyLayout[this.curLayout]);

        if (this.curLayout == LobbyLayout.SINGLE) {
            this.SetLayout(LobbyLayout.MULTI); // force? onResize?

            // button click
            AppAudioPlayer.PlaySfx(SoundEffectKeys.click5);
        }
    }

    private OnFullscreenClick(): void {
        // console.log("LobbyScene::OnFullscreenClick");

        // toggle click
        AppAudioPlayer.PlaySfx(SoundEffectKeys.click3);

        this.scene.scale.toggleFullscreen();
    }

    private OnMusicClick(): void {
        // console.log("LobbyScene::OnMusicClick");

        // 
        this.btnMusic.Toggle();

        // play button click
        AppAudioPlayer.PlaySfx(SoundEffectKeys.click3);

        // save to session data
        const isMusicEnabled: boolean = this.btnMusic.IsSelected();
        AppAudioPlayer.SetMusicEnabled(isMusicEnabled);
    }

    private OnSoundClick(): void {
        // console.log("LobbyScene::OnSoundClick");

        // 
        this.btnSound.Toggle();

        // play button click
        AppAudioPlayer.PlaySfx(SoundEffectKeys.click3);

        // save to session data
        const isSoundsEnabled: boolean = this.btnSound.IsSelected();
        AppAudioPlayer.SetSoundsEnabled(isSoundsEnabled);
    }

    public PrevItem(): void {
        this.curItemIndex = GetFixedCollectionIndex(this.items, this.curItemIndex - 1);

        const screenOrientationType: ScreenOrientationType = ScreenManager.GetOrientation();
        this.UpdateItemsVisibility();
        this.UpdateItemsTransforms(screenOrientationType);
        this.UpdateItemsInteractive();

        // click
        AppAudioPlayer.PlaySfx(SoundEffectKeys.click4);
    }

    public NextItem(): void {
        // console.log(pointer.downX, pointer.downY, pointer.distance);
        // console.log(pointer.getDistance())

        this.curItemIndex = GetFixedCollectionIndex(this.items, this.curItemIndex + 1);

        const screenOrientationType: ScreenOrientationType = ScreenManager.GetOrientation();
        this.UpdateItemsVisibility();
        this.UpdateItemsTransforms(screenOrientationType);
        this.UpdateItemsInteractive();

        // click
        AppAudioPlayer.PlaySfx(SoundEffectKeys.click4);
    }

    public OnItemHelpClicked(): void {
        const _this: LobbyScreen = this["_this"];
        const item: LobbyVariationItem = this["item"];

        _this.onLobbyVariationHelpClick?.(item);

        // click
        AppAudioPlayer.PlaySfx(SoundEffectKeys.click3);
    }

    public OnItemStatsClicked(): void {
        const _this: LobbyScreen = this["_this"];
        const item: LobbyVariationItem = this["item"];

        _this.onLobbyVariationStatsClick?.(item);

        // click
        AppAudioPlayer.PlaySfx(SoundEffectKeys.click3);
    }

    public GetScene(): Phaser.Scene {
        return this.scene;
    }

    protected OnShowStart(animated: boolean): void {
        // console.log("OnShowStart");

        // this.alpha = 1;
        
        // GTMTracking
        GTMTracking.PLDTrackingMainMenu();

        // use-case: coming back from Table to Lobby
        this.UpdateItemsInteractive();

        // orientation first: ensures correct reposition from orienation switches in Table screen on followup resize.
        this.OnOrientationChangedManual();
        this.OnResizeManual();
        this.RegisterListeners();

        // show screen
        this.scene.tweens.killTweensOf(this);
        const scrTransData = ScreenManager.GetScreenTransitionPositions(this.transitionDirection);
        let duration: number = OptionsDataManager.GetTiming(AppTimings.screenShowDuration);
        if (animated) {
            this.x = scrTransData.startX;
            this.visible = true;
            this.scene.tweens.add({
                targets: this,
                x: scrTransData.endX,
                duration: duration,
                ease: AppSettings.ease.screenShowHideEase,
            });
        }
        else {
            this.x = scrTransData.endX;
        }
    }

    protected OnHideStart(animated: boolean): void {
        // console.log("OnHideStart");

        // onComplete: visible =false
        this.UnregisterListeners();

        // hide screen
        this.scene.tweens.killTweensOf(this);
        const scrTransData = ScreenManager.GetScreenTransitionPositions(this.transitionDirection);
        let duration: number = OptionsDataManager.GetTiming(AppTimings.screenHideDuration);
        if (animated) {
            this.x = scrTransData.startX;
            this.scene.tweens.add({
                targets: this,
                x: scrTransData.endX,
                duration: duration,
                ease: AppSettings.ease.screenShowHideEase,
                onComplete: () => {
                    this.visible = false;
                },
            });
        }
        else {
            this.x = scrTransData.endX;
            this.visible = false;
        }
    }

    protected RegisterListeners(): void {
        // console.log("LobbyScreen::RegisterListeners");

        this.btnBurger.on(ButtonEvents.CLICK, this.burgerClickCallback, this);
        this.btnBack?.on(ButtonEvents.CLICK, this.OnBackClick, this);
        this.btnPrev?.on(ButtonEvents.CLICK, this.PrevItem, this);
        this.btnNext?.on(ButtonEvents.CLICK, this.NextItem, this);
        this.items.forEach(item => {
            item.on(Phaser.Input.Events.GAMEOBJECT_POINTER_UP, this.OnItemClicked, { _this: this, item: item });
            item.btnHelp.on(ButtonEvents.CLICK, this.OnItemHelpClicked, { _this: this, item: item });
            item.btnStats.on(ButtonEvents.CLICK, this.OnItemStatsClicked, { _this: this, item: item });
        });
        this.btnNewGame.on(ButtonEvents.CLICK, this.OnNewGameClickCallback, this);
        this.btnContinue.on(ButtonEvents.CLICK, this.OnContinueClickCallback, this);
        if (this.btnFullscreen) this.btnFullscreen.on(ButtonEvents.CLICK, this.OnFullscreenClick, this);
        this.btnMusic.on(ButtonEvents.CLICK, this.OnMusicClick, this);
        this.btnSound.on(ButtonEvents.CLICK, this.OnSoundClick, this);

        ScreenManager.GetEventEmitter().on(ScreenManagerEvents.RESIZE, this.OnResize, this);
        ScreenManager.GetEventEmitter().on(ScreenManagerEvents.ORIENTATION_CHANGE, this.OnOrientationChanged, this);
    }

    protected UnregisterListeners(): void {
        // console.log("LobbyScreen::UnregisterListeners");

        this.btnBurger.off(ButtonEvents.CLICK, this.burgerClickCallback);
        this.btnBack?.off(ButtonEvents.CLICK, this.OnBackClick);
        this.btnPrev?.off(ButtonEvents.CLICK, this.PrevItem);
        this.btnNext?.off(ButtonEvents.CLICK, this.NextItem);
        this.items.forEach(item => {
            item.off(Phaser.Input.Events.GAMEOBJECT_POINTER_UP, this.OnItemClicked);
            item.btnHelp.off(ButtonEvents.CLICK, this.OnItemHelpClicked);
            item.btnStats.off(ButtonEvents.CLICK, this.OnItemStatsClicked);
        });
        this.btnNewGame.off(ButtonEvents.CLICK, this.OnNewGameClickCallback);
        this.btnContinue.off(ButtonEvents.CLICK, this.OnContinueClickCallback);
        if (this.btnFullscreen) this.btnFullscreen.off(ButtonEvents.CLICK, this.OnFullscreenClick);
        this.btnMusic.off(ButtonEvents.CLICK, this.OnMusicClick);
        this.btnSound.off(ButtonEvents.CLICK, this.OnSoundClick);

        ScreenManager.GetEventEmitter().off(ScreenManagerEvents.RESIZE, this.OnResize);
        ScreenManager.GetEventEmitter().off(ScreenManagerEvents.ORIENTATION_CHANGE, this.OnOrientationChanged);
    }

    private OnContinueClickCallback(): void {
        // 
        this.continueClickCallback?.();
    }

    private OnNewGameClickCallback(): void {
        // 
        this.newGameClickCallback?.();
    }

    /**
     * This triggers from LiveDataChangeEvents.POINTS_PER_GAME_CHANGE emit listener,
     * which only emits from OptionsDataManager's ResolveLiveDataChange->ResolveOptionsItemDataChange,
     * which unfortunately triggers via different UIs.
     * @param newChoiceIndex 
     */
    private OnPointsPerGameChange(newChoiceIndex: number): void {
        // console.log("OnPointsPerGameChange", newChoiceIndex);

        this.items.forEach(item => {
            item.slider.SetChoiceIndex(newChoiceIndex);
        });

        // set options screen to dirty (will reload its items with live data next time it opens)
        // NOTE: this triggers through OptionsMAnager emit also when changing from Options screen (unfortunate, but harder to solve)
        // EDIT: directly from the onyl place where it matters: LobbyVariationItem
        // (this.scene as GameScene).optionsScreen.SetDirty(true);
    }

    protected OnResizeManual(fromLayoutSwitch: boolean = false): void {
        // process resize
        const windowRect: RectSize = ScreenManager.GetWindowRect();
        this.OnResize(windowRect, fromLayoutSwitch);
    }

    private OnResize(windowRect: RectSize, fromLayoutSwitch: boolean = false): void {
        // console.log("LobbyScreen::OnResize", fromLayoutSwitch);

        const screenOrientationType: ScreenOrientationType = ScreenManager.GetOrientation();
        // const assetScale: number = ScreenManager.GetAssetScale(); // baseScale?
        const screenCenterX: number = (0.5 * windowRect.width) | 0;
        const verNumScale = ScreenManager.GetAssetScale(1.0, 0.6);

        // versionNumber
        this.versionNumber.scale = verNumScale;
        this.versionNumber.setPosition(windowRect.width - 10 * verNumScale, windowRect.height - 10 * verNumScale);

        let contentScale: number;
        let continueBtnScale: number;

        const headerItemScale = ScreenManager.GetAssetScale(
            LayoutDataManager.GetSharedData().general.screen.closeButton.baseScale,
            LayoutDataManager.GetSharedData().general.screen.closeButton.scaleBias,
        );
        const headerItemOffset: number = this.headerProps.btnOffset * headerItemScale;
        const headerItemSpacing: number = this.headerProps.btnSpacing * headerItemScale;
        this.btnBurger.setPosition(windowRect.width - headerItemOffset, headerItemOffset);
        if ((this.curLayout == LobbyLayout.SINGLE) && !fromLayoutSwitch) {
            this.btnBurger.scale = headerItemScale;
        }
        this.btnMusic.scale = headerItemScale;
        this.btnSound.scale = headerItemScale;
        this.btnFullscreen?.setScale(headerItemScale);

        // Position based on orientation
        let contPosY: number;
        switch (screenOrientationType) {
            case ScreenOrientationType.LANDSCAPE:
                switch (this.curLayout) {
                    case LobbyLayout.MULTI:
                        contPosY = windowRect.height * 0.5;
                        contentScale = ScreenManager.GetAssetScale(1.0, 1.0);
                        // continueBtnScale = ScreenManager.GetAssetScale(1.0, 1.0);

                        // do not resize from layout switch (there we animate)
                        if (!fromLayoutSwitch) {
                            this.btnMusic.setPosition(windowRect.width - headerItemOffset, headerItemOffset);
                            this.btnSound.setPosition(windowRect.width - headerItemOffset - headerItemSpacing, headerItemOffset);
                            this.btnFullscreen?.setPosition(windowRect.width - headerItemOffset - 2 * headerItemSpacing, headerItemOffset);
                        }
                        break;
                    case LobbyLayout.SINGLE:
                        contPosY = windowRect.height * 0.42;
                        contentScale = ScreenManager.GetAssetScale(1.0, 1.0);
                        continueBtnScale = ScreenManager.GetAssetScale(1.0, 1.0);

                        // do not resize from layout switch (there we animate)
                        if (!fromLayoutSwitch) {
                            this.btnMusic.setPosition(windowRect.width - headerItemOffset - headerItemSpacing, headerItemOffset);
                            this.btnSound.setPosition(windowRect.width - headerItemOffset - 2 * headerItemSpacing, headerItemOffset);
                            this.btnFullscreen?.setPosition(windowRect.width - headerItemOffset - 3 * headerItemSpacing, headerItemOffset);
                            if (this.btnBack) {
                                // console.log(this.scene.scale.isFullscreen, IsIPad())
                                this.btnBack.setScale(headerItemScale);
                                this.btnBack.setPosition(
                                    this.scene.scale.isFullscreen && IsIPad() ?
                                        AppSettings.layout.IPAD_UI_TL_FS_OFFSET_X + 90 * headerItemScale :
                                        90 * headerItemScale,
                                    90 * headerItemScale,
                                );
                            }
                        }

                        const curFocusedItemY = this.items[this.curItemIndex].y;

                        this.btnNewGame.setPosition(
                            screenCenterX - (20 + this.btnNewGame.view.displayWidth * 0.5) * continueBtnScale,
                            contPosY + (curFocusedItemY + 500) * continueBtnScale,
                        );
                        this.btnContinue.setPosition(
                            screenCenterX + (20 + this.btnNewGame.view.displayWidth * 0.5) * continueBtnScale,
                            contPosY + (curFocusedItemY + 500) * continueBtnScale,
                        );
                        if (!fromLayoutSwitch) {
                            this.btnNewGame.scale = continueBtnScale;
                            this.btnContinue.scale = continueBtnScale;
                        }
                        break;
                }
                this.itemsContainer.setPosition(screenCenterX, contPosY);
                this.itemsContainer.scale = contentScale;
                break;

            case ScreenOrientationType.PORTRAIT:
                switch (this.curLayout) {
                    case LobbyLayout.MULTI:
                        contPosY = windowRect.height * 0.5;
                        contentScale = ScreenManager.GetAssetScale(0.9, 1.0);
                        // continueBtnScale = ScreenManager.GetAssetScale(1.0, 1.0);

                        // do not resize from layout switch (there we animate)
                        if (!fromLayoutSwitch) {
                            this.btnMusic.setPosition(windowRect.width - headerItemOffset, headerItemOffset);
                            this.btnSound.setPosition(windowRect.width - headerItemOffset - headerItemSpacing, headerItemOffset);
                            this.btnFullscreen?.setPosition(windowRect.width - headerItemOffset - 2 * headerItemSpacing, headerItemOffset);
                        }
                        break;
                    case LobbyLayout.SINGLE:
                        contPosY = windowRect.height * 0.4;
                        contentScale = ScreenManager.GetAssetScale(1.2, 1.0);
                        continueBtnScale = ScreenManager.GetAssetScale(1.4, 1.0);

                        // do not resize from layout switch (there we animate)
                        if (!fromLayoutSwitch) {
                            this.btnMusic.setPosition(windowRect.width - headerItemOffset - headerItemSpacing, headerItemOffset);
                            this.btnSound.setPosition(windowRect.width - headerItemOffset - 2 * headerItemSpacing, headerItemOffset);
                            this.btnFullscreen?.setPosition(windowRect.width - headerItemOffset - 3 * headerItemSpacing, headerItemOffset);
                            if (this.btnBack) {
                                // console.log(this.scene.scale.isFullscreen, IsIPad())
                                this.btnBack.setScale(headerItemScale);
                                this.btnBack.setPosition(
                                    this.scene.scale.isFullscreen && IsIPad() ?
                                        AppSettings.layout.IPAD_UI_TL_FS_OFFSET_X + 90 * headerItemScale :
                                        90 * headerItemScale,
                                    90 * headerItemScale,
                                );
                            }
                        }

                        const curFocusedItemY = this.items[this.curItemIndex].y;
                        const newGamePortraitBaseScale = 1.4;

                        this.btnNewGame.setPosition(
                            screenCenterX,
                            contPosY + (curFocusedItemY + 650) * contentScale
                        );
                        this.btnContinue.setPosition(
                            screenCenterX,
                            contPosY + (curFocusedItemY + 800) * contentScale +
                            this.btnNewGame.view.displayHeight / 2 * continueBtnScale * newGamePortraitBaseScale
                        );
                        if (!fromLayoutSwitch) {
                            this.btnNewGame.scale = continueBtnScale * newGamePortraitBaseScale;
                            this.btnContinue.scale = continueBtnScale * newGamePortraitBaseScale;
                        }
                        break;
                }
                this.itemsContainer.setPosition(screenCenterX, contPosY);
                this.itemsContainer.scale = contentScale;
                break;
        }

        // this.guide.copyPosition(this.itemsContainer);
    }

    private OnOrientationChangedManual(): void {
        const curOrientation: ScreenOrientationType = ScreenManager.GetOrientation();
        this.OnOrientationChanged(curOrientation);
    }

    private OnOrientationChanged(screenOrientationType: ScreenOrientationType): void {
        // console.log("LobbyScreen::OnOrientationChanged",ScreenOrientationType[screenOrientationType]);

        // show/hide burger
        this.UpdateItemsVisibility();
        this.UpdateItemsTransforms(screenOrientationType, false); // animate?
    }
}